import {
	AlertTriangle,
	CheckCircle2,
	CircleHelp,
	Clock3,
	ExternalLink,
	FileSearch,
	Gauge,
	Layers3,
	Scale,
	Users,
	XCircle,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { formatDate } from "~/lib/format";
import {
	TENDER_SOURCE_LABELS,
	type TenderIntelligence,
	type TenderIntelligenceEvidence,
} from "~/lib/tenders";

type Lot = TenderIntelligence["lots"][number];
type Participant = TenderIntelligence["participants"][number];
type Submission = TenderIntelligence["submissions"][number];
type OfferValue = TenderIntelligence["offerValues"][number];
type Event = TenderIntelligence["events"][number];

const OUTCOME_LABELS: Record<string, string> = {
	IN_PROGRESS: "Řízení probíhá",
	CONTRACT_CONCLUDED: "Smlouva uzavřena",
	FRAMEWORK_CONCLUDED: "Rámcová dohoda uzavřena",
	DPS_ESTABLISHED: "Dynamický nákupní systém zaveden",
	PERFORMANCE_FINISHED: "Plnění dokončeno",
	CANCELLED: "Část zrušena",
	UNSUCCESSFUL: "Řízení bez výsledku",
	UNKNOWN: "Výsledek není ve zdroji doložen",
};

const EVENT_LABELS: Record<string, string> = {
	QUALIFICATION_PASSED: "Kvalifikace doloženě splněna",
	QUALIFICATION_FAILED: "Kvalifikace nesplněna",
	EXCLUDED: "Účastník vyloučen",
	PARTICIPATION_CEASED: "Účast ukončena",
	WITHDRAWN: "Nabídka vzata zpět",
	SELECTED: "Účastník vybrán",
	SELECTION_REVOKED: "Výběr zrušen",
	CONTRACTED: "Smlouva uzavřena",
	CONTRACT_DECLINED: "Uzavření smlouvy odmítnuto",
	ADMITTED_TO_DPS: "Přijat do dynamického nákupního systému",
	REMOVED_FROM_DPS: "Vyřazen z dynamického nákupního systému",
	LOT_CANCELLED: "Část zakázky zrušena",
};

const VALUE_KIND_LABELS: Record<string, string> = {
	PRICE: "Nabídková cena",
	QUALITY: "Hodnota kvalitativního kritéria",
	LIFECYCLE_COST: "Náklady životního cyklu",
	ITEM_PRICE: "Položková cena",
	SUBCONTRACTING_VALUE: "Hodnota subdodávky",
	OTHER: "Jiná hodnota nabídky",
};

const PRICE_BASIS_LABELS: Record<string, string> = {
	TOTAL: "celková cena",
	ANNUAL: "roční cena",
	MONTHLY: "měsíční cena",
	UNIT: "jednotková cena",
	ITEM: "cena položky",
	RATE: "sazba",
	PERCENTAGE: "procentní hodnota",
	FRAMEWORK_MAXIMUM: "maximální hodnota rámce",
	NOT_MONETARY: "nepeněžní hodnota",
	UNKNOWN: "základ ceny neuveden",
};

const VAT_BASIS_LABELS: Record<string, string> = {
	WITHOUT_VAT: "bez DPH",
	WITH_VAT: "s DPH",
	NOT_APPLICABLE: "DPH se nepoužije",
	UNKNOWN: "režim DPH neuveden",
};

const CONFLICT_FIELD_LABELS: Record<string, string> = {
	originalValue: "nabídková cena",
	isWinner: "vítěz části",
	count: "počet podání",
	outcome: "výsledek části",
	lotMapping: "přiřazení části",
	partyIdentity: "identita účastníka",
};

const MONETARY_VALUE_KINDS = new Set([
	"PRICE",
	"LIFECYCLE_COST",
	"ITEM_PRICE",
	"SUBCONTRACTING_VALUE",
]);

function sourceLabel(source: string): string {
	return TENDER_SOURCE_LABELS[source.toLowerCase()] ?? source.toUpperCase();
}

function exactDecimalIsPositive(value: string | null): boolean {
	if (!value || value.trim().startsWith("-")) return false;
	return /[1-9]/.test(value);
}

function formatExactDecimal(value: string): string {
	const [integer, fraction] = value.split(".");
	const sign = integer.startsWith("-") ? "−" : "";
	const digits = integer.replace(/^[+-]/, "");
	const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
	return `${sign}${grouped}${fraction ? `,${fraction}` : ""}`;
}

function formatExactAmount(value: string, currency: string | null): string {
	const unit = currency?.toUpperCase() === "CZK" ? "Kč" : currency?.toUpperCase();
	return `${formatExactDecimal(value)} ${unit ?? "(měna neuvedena)"}`;
}

function confidenceLabel(confidence: string | null): string | null {
	if (!confidence) return null;
	const numeric = Number(confidence);
	if (!Number.isFinite(numeric)) return null;
	return `Důvěra ${(numeric * 100).toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} %`;
}

function Evidence({ evidence }: { evidence: TenderIntelligenceEvidence }) {
	const confidence = confidenceLabel(evidence.confidence);
	const url = evidence.sourceUrl ?? evidence.documentUrl;

	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
			<span className="inline-flex items-center gap-1">
				<FileSearch className="h-3.5 w-3.5" /> {sourceLabel(evidence.source)} · zdrojový doklad
			</span>
			{confidence && <span>{confidence}</span>}
			{url && (
				<a
					href={url}
					target="_blank"
					rel="noopener noreferrer nofollow"
					className="inline-flex items-center gap-1 text-primary hover:underline"
				>
					Otevřít doklad <ExternalLink className="h-3 w-3" />
				</a>
			)}
		</div>
	);
}

function ParticipationStatus({ participant }: { participant: Participant }) {
	if (participant.participationConditionsMet === true) {
		return (
			<span className="badge badge-success badge-soft badge-sm gap-1">
				<CheckCircle2 className="h-3 w-3" /> Podmínky účasti doloženě splněny
			</span>
		);
	}
	if (participant.participationConditionsMet === false) {
		return (
			<span className="badge badge-error badge-soft badge-sm gap-1">
				<XCircle className="h-3 w-3" /> Podmínky účasti nesplněny podle zdroje
			</span>
		);
	}
	return (
		<span className="badge badge-ghost badge-sm gap-1">
			<CircleHelp className="h-3 w-3" /> Splnění podmínek zdroj nedokládá
		</span>
	);
}

function OfferValueRow({
	value,
	intelligence,
}: {
	value: OfferValue;
	intelligence: TenderIntelligence;
}) {
	const criterion = intelligence.criteria.find((item) => item.id === value.criterionId);
	const monetary = MONETARY_VALUE_KINDS.has(value.kind);
	const isValidMonetaryValue = monetary && exactDecimalIsPositive(value.originalValue);
	const hasDisplayValue = monetary ? isValidMonetaryValue : value.originalValue != null;

	return (
		<div className="rounded-lg bg-gray-50 p-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="font-medium">
						{criterion?.criterionName ?? VALUE_KIND_LABELS[value.kind] ?? "Hodnota nabídky"}
					</p>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{VALUE_KIND_LABELS[value.kind] ?? value.kind} ·{" "}
						{PRICE_BASIS_LABELS[value.priceBasis] ?? "základ ceny neuveden"} ·{" "}
						{VAT_BASIS_LABELS[value.vatBasis] ?? "režim DPH neuveden"}
					</p>
				</div>
				{hasDisplayValue ? (
					<p className="font-mono text-base font-semibold tabular-nums">
						{monetary
							? formatExactAmount(value.originalValue as string, value.currency)
							: formatExactDecimal(value.originalValue as string)}
						{value.unit && !monetary ? ` ${value.unit}` : ""}
					</p>
				) : (
					<span className="badge badge-warning badge-soft badge-sm">
						{monetary ? "Platná kladná cena není doložena" : "Hodnota není doložena"}
					</span>
				)}
			</div>
			{value.quantity && (
				<p className="mt-2 text-xs text-muted-foreground">
					Množství: <span className="font-mono">{formatExactDecimal(value.quantity)}</span>{" "}
					{value.unit ?? "(jednotka neuvedena)"}
				</p>
			)}
			{value.currencyAssumed && (
				<p className="mt-2 text-xs text-warning-content">
					Měna nebyla uvedena přímo u hodnoty a byla doplněna podle zdroje.
				</p>
			)}
			{value.missingReason && (
				<p className="mt-2 text-xs text-muted-foreground">
					Důvod chybějící hodnoty: <span className="font-mono">{value.missingReason}</span>
				</p>
			)}
			<div className="mt-3">
				<Evidence evidence={value.evidence} />
			</div>
		</div>
	);
}

function SubmissionCard({
	submission,
	intelligence,
}: {
	submission: Submission;
	intelligence: TenderIntelligence;
}) {
	const values = intelligence.offerValues.filter((value) => value.submissionId === submission.id);
	const events = intelligence.events.filter((event) => event.submissionId === submission.id);
	const withdrew =
		submission.withdrawnAt || events.some((event) => event.eventType === "WITHDRAWN");
	const exclusion = events.find((event) => event.eventType === "EXCLUDED");

	return (
		<div className="rounded-lg bg-white p-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="text-sm font-medium">Nabídka</p>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{submission.submittedAt
							? `Podána ${formatDate(submission.submittedAt)}`
							: "Datum podání neuvedeno"}
						{submission.roundNumber != null ? ` · kolo ${submission.roundNumber}` : ""}
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					{submission.rank != null && (
						<span className="badge badge-primary badge-soft badge-sm">
							Pořadí {submission.rank}.
						</span>
					)}
					{submission.evaluated === true && (
						<span className="badge badge-success badge-soft badge-sm">Hodnocena</span>
					)}
					{submission.evaluated === false && (
						<span className="badge badge-ghost badge-sm">Nehodnocena</span>
					)}
					{withdrew && <span className="badge badge-warning badge-soft badge-sm">Vzata zpět</span>}
					{exclusion && <span className="badge badge-error badge-soft badge-sm">Vyloučena</span>}
				</div>
			</div>
			{exclusion?.reasonCode && (
				<p className="mt-3 text-xs text-muted-foreground">
					Kód důvodu vyloučení: <span className="font-mono">{exclusion.reasonCode}</span>
				</p>
			)}
			{submission.offerValueMissingReason && values.length === 0 && (
				<p className="mt-3 text-xs text-muted-foreground">
					Důvod chybějící ceny:{" "}
					<span className="font-mono">{submission.offerValueMissingReason}</span>
				</p>
			)}
			{values.length > 0 ? (
				<div className="mt-4 flex flex-col gap-3">
					{values.map((value) => (
						<OfferValueRow key={value.id} value={value} intelligence={intelligence} />
					))}
				</div>
			) : (
				<p className="mt-3 text-sm text-muted-foreground">
					Zdroj neobsahuje použitelnou hodnotu nabídky.
				</p>
			)}
			<div className="mt-3">
				<Evidence evidence={submission.evidence} />
			</div>
		</div>
	);
}

function ParticipantCard({
	participant,
	intelligence,
}: {
	participant: Participant;
	intelligence: TenderIntelligence;
}) {
	const members = intelligence.members.filter((member) => member.participantId === participant.id);
	const submissions = intelligence.submissions.filter(
		(submission) => submission.participantId === participant.id,
	);

	return (
		<article className="rounded-xl bg-gray-50 p-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="font-semibold">
						{participant.displayName ?? "Účastník bez zveřejněného názvu"}
					</p>
					<p className="mt-0.5 font-mono text-xs text-muted-foreground">
						{participant.primaryIco ? `IČO ${participant.primaryIco}` : "IČO neuvedeno"}
						{participant.country ? ` · ${participant.country}` : ""}
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					{participant.isConsortium && (
						<span className="badge badge-primary badge-soft badge-sm">Sdružení dodavatelů</span>
					)}
					<ParticipationStatus participant={participant} />
				</div>
			</div>

			{members.length > 0 && (
				<div className="mt-4 rounded-lg bg-white p-3">
					<p className="text-xs font-medium text-muted-foreground">Členové sdružení</p>
					<ul className="mt-2 flex flex-col gap-2 text-sm">
						{members.map((member) => (
							<li key={member.id} className="flex flex-wrap items-center justify-between gap-2">
								<span>
									{member.name ?? "Člen bez zveřejněného názvu"}
									{member.ico && (
										<span className="ml-2 font-mono text-xs text-muted-foreground">
											IČO {member.ico}
										</span>
									)}
								</span>
								{member.isLeader && (
									<span className="badge badge-ghost badge-sm">Vedoucí člen</span>
								)}
							</li>
						))}
					</ul>
				</div>
			)}

			{submissions.length > 0 ? (
				<div className="mt-4 flex flex-col gap-3">
					{submissions.map((submission) => (
						<SubmissionCard
							key={submission.id}
							submission={submission}
							intelligence={intelligence}
						/>
					))}
				</div>
			) : (
				<p className="mt-3 text-sm text-muted-foreground">
					K účastníkovi není doložena samostatná nabídka.
				</p>
			)}
			<div className="mt-3">
				<Evidence evidence={participant.evidence} />
			</div>
		</article>
	);
}

function EventRow({ event, intelligence }: { event: Event; intelligence: TenderIntelligence }) {
	const participant = intelligence.participants.find((item) => item.id === event.participantId);
	const eventDate = event.eventAt ?? event.decisionAt ?? event.contractSignedAt;
	const contractValue = exactDecimalIsPositive(event.contractValueWithoutVat)
		? formatExactAmount(event.contractValueWithoutVat as string, event.contractCurrency)
		: null;

	return (
		<li className="relative grid grid-cols-[1.25rem_1fr] gap-3 pb-5 last:pb-0">
			<span className="mt-1 h-3 w-3 rounded-full bg-primary ring-4 ring-primary/10" />
			<div>
				<div className="flex flex-wrap items-baseline justify-between gap-2">
					<p className="font-medium">{EVENT_LABELS[event.eventType] ?? event.eventType}</p>
					<time className="font-mono text-xs text-muted-foreground">
						{eventDate ? formatDate(eventDate) : "Datum ve zdroji chybí"}
					</time>
				</div>
				{participant && (
					<p className="mt-1 text-sm text-muted-foreground">
						{participant.displayName ?? participant.primaryIco ?? "Neuvedený účastník"}
					</p>
				)}
				{event.reasonCode && (
					<p className="mt-1 text-xs text-muted-foreground">
						Kód důvodu: <span className="font-mono">{event.reasonCode}</span>
					</p>
				)}
				{contractValue && (
					<p className="mt-1 font-mono text-sm font-medium tabular-nums">{contractValue} bez DPH</p>
				)}
				<div className="mt-2">
					<Evidence evidence={event.evidence} />
				</div>
			</div>
		</li>
	);
}

function LotPanel({ lot, intelligence }: { lot: Lot; intelligence: TenderIntelligence }) {
	const participants = intelligence.participants.filter(
		(participant) => participant.lotId === lot.id,
	);
	const events = intelligence.events.filter((event) => event.lotId === lot.id);
	const counts = intelligence.submissionCounts.filter((count) => count.lotId === lot.id);
	const withheld = intelligence.withheldFields.filter(
		(field) => field.lotId === lot.id || field.lotId == null,
	);

	return (
		<div className="flex flex-col gap-6 pt-4">
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr]">
				<div>
					<p className="text-xs font-semibold text-primary">Část {lot.lotNumber}</p>
					<h3 className="mt-1 text-xl font-semibold">
						{lot.title ?? "Část bez zveřejněného názvu"}
					</h3>
					<div className="mt-3 flex flex-wrap gap-2">
						<span className="badge badge-soft">{OUTCOME_LABELS[lot.outcome] ?? lot.outcome}</span>
						{lot.isFramework && (
							<span className="badge badge-primary badge-soft">Rámcová dohoda</span>
						)}
						{lot.establishesDps && (
							<span className="badge badge-primary badge-soft">Dynamický nákupní systém</span>
						)}
					</div>
				</div>
				<dl className="grid grid-cols-2 gap-3 rounded-xl bg-gray-50 p-4 text-sm">
					<div>
						<dt className="text-xs text-muted-foreground">Hlavní CPV</dt>
						<dd className="font-mono">{lot.mainCpv ?? "–"}</dd>
					</div>
					<div>
						<dt className="text-xs text-muted-foreground">Lhůta pro nabídky</dt>
						<dd>{formatDate(lot.submissionDeadline)}</dd>
					</div>
					<div>
						<dt className="text-xs text-muted-foreground">Předpokládaná hodnota</dt>
						<dd className="font-mono tabular-nums">
							{lot.estimatedValue && exactDecimalIsPositive(lot.estimatedValue)
								? formatExactAmount(lot.estimatedValue, lot.estimatedValueCurrency)
								: "–"}
						</dd>
					</div>
					<div>
						<dt className="text-xs text-muted-foreground">Přijaté nabídky</dt>
						<dd className="font-mono tabular-nums">{lot.receivedSubmissionsCount ?? "–"}</dd>
					</div>
				</dl>
			</div>
			<Evidence evidence={lot.evidence} />

			{counts.length > 0 && (
				<section className="flex flex-wrap gap-2" aria-label="Počty podání podle zdrojů">
					{counts.map((count) => (
						<span key={count.id} className="badge badge-ghost">
							{count.sourceLabel}: {count.count.toLocaleString("cs-CZ")}
						</span>
					))}
				</section>
			)}

			<section aria-labelledby={`participants-${lot.id}`}>
				<h4 id={`participants-${lot.id}`} className="flex items-center gap-2 text-lg font-semibold">
					<Users className="h-5 w-5 text-primary" /> Účastníci a nabídky
				</h4>
				{participants.length > 0 ? (
					<div className="mt-3 flex flex-col gap-3">
						{participants.map((participant) => (
							<ParticipantCard
								key={participant.id}
								participant={participant}
								intelligence={intelligence}
							/>
						))}
					</div>
				) : (
					<p className="mt-3 text-sm text-muted-foreground">
						Ve veřejných zdrojích nejsou pro tuto část doloženi účastníci.
					</p>
				)}
			</section>

			<section aria-labelledby={`timeline-${lot.id}`}>
				<h4 id={`timeline-${lot.id}`} className="flex items-center gap-2 text-lg font-semibold">
					<Clock3 className="h-5 w-5 text-primary" /> Průběh řízení
				</h4>
				{events.length > 0 ? (
					<ol className="mt-4">
						{events.map((event) => (
							<EventRow key={event.id} event={event} intelligence={intelligence} />
						))}
					</ol>
				) : (
					<p className="mt-3 text-sm text-muted-foreground">
						Zdroj neobsahuje datované kroky účastníků pro tuto část.
					</p>
				)}
			</section>

			{withheld.length > 0 && (
				<div className="alert alert-warning items-start" role="note">
					<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
					<div>
						<p className="font-medium">Některé údaje nejsou veřejně zobrazené</p>
						<p className="mt-1 text-sm">
							Zdroj nebo pravidla ochrany údajů omezují pole:{" "}
							{withheld.map((field) => field.fieldCode).join(", ")}.
						</p>
					</div>
				</div>
			)}
		</div>
	);
}

export function TenderIntelligencePanel({
	intelligence,
}: {
	intelligence: TenderIntelligence | null | undefined;
}) {
	if (!intelligence) {
		return (
			<section className="card-surface rounded-xl p-5" aria-labelledby="intelligence-heading">
				<h2 id="intelligence-heading" className="flex items-center gap-2 text-lg font-semibold">
					<Layers3 className="h-5 w-5 text-primary" /> Podrobný průběh zakázky
				</h2>
				<p className="mt-2 text-sm text-muted-foreground">
					Strukturované údaje o částech, účastnících a hodnocení pro tuto zakázku zatím nejsou
					dostupné. Základní údaje níže zůstávají beze změny.
				</p>
			</section>
		);
	}

	const unresolvedConflicts = intelligence.conflicts.filter(
		(conflict) => conflict.resolutionStatus === "UNRESOLVED",
	);
	const unresolvedFields = [
		...new Set(
			unresolvedConflicts.map(
				(conflict) => CONFLICT_FIELD_LABELS[conflict.factField] ?? conflict.factField,
			),
		),
	];
	const truncatedCollections = Object.entries(intelligence.truncated)
		.filter(([, value]) => value)
		.map(([key]) => key);

	return (
		<section className="card-surface rounded-xl p-5 sm:p-6" aria-labelledby="intelligence-heading">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="text-sm font-semibold text-primary">Doložená data z průběhu řízení</p>
					<h2 id="intelligence-heading" className="mt-1 text-xl font-semibold">
						Části, účastníci a hodnocení
					</h2>
				</div>
				<span className="badge badge-soft gap-1">
					<Gauge className="h-3.5 w-3.5" /> Aktualizováno {formatDate(intelligence.generatedAt)}
				</span>
			</div>

			{unresolvedConflicts.length > 0 && (
				<div className="alert alert-warning mt-4 items-start" role="alert">
					<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
					<div>
						<p className="font-medium">Zdroje se u některých údajů rozcházejí</p>
						<p className="mt-1 text-sm">
							Sporné údaje nezobrazujeme jako jistý výsledek. Kontrolu vyžadují:{" "}
							{unresolvedFields.join(", ")}.
						</p>
					</div>
				</div>
			)}

			{truncatedCollections.length > 0 && (
				<div className="alert mt-4 items-start" role="note">
					<CircleHelp className="mt-0.5 h-5 w-5 shrink-0" />
					<div>
						<p className="font-medium">Velký datový soubor je zobrazený jen částečně</p>
						<p className="mt-1 text-sm">
							Rozhraní omezilo {truncatedCollections.length.toLocaleString("cs-CZ")} datových
							kolekcí. Úplný stav ověřte v odkazovaných zdrojových dokladech.
						</p>
					</div>
				</div>
			)}

			{intelligence.lots.length > 0 ? (
				<Tabs defaultValue={intelligence.lots[0].id} className="mt-5">
					<TabsList aria-label="Části zakázky">
						{intelligence.lots.map((lot) => (
							<TabsTrigger key={lot.id} value={lot.id}>
								Část {lot.lotNumber}
							</TabsTrigger>
						))}
					</TabsList>
					{intelligence.lots.map((lot) => (
						<TabsContent key={lot.id} value={lot.id}>
							<LotPanel lot={lot} intelligence={intelligence} />
						</TabsContent>
					))}
				</Tabs>
			) : (
				<p className="mt-4 text-sm text-muted-foreground">
					Zdroj zatím neobsahuje samostatně identifikované části nebo celek zakázky.
				</p>
			)}

			<div className="mt-6 rounded-xl bg-primary/5 p-4">
				<h3 className="flex items-center gap-2 font-semibold">
					<Scale className="h-4 w-4 text-primary" /> Jak porovnáváme ceny
				</h3>
				<p className="mt-2 text-sm text-muted-foreground">
					Srovnatelná skupina musí mít stejnou položku nebo CPV, jednotku, režim DPH, cenový základ,
					fázi řízení a podobné období. Rámcové maximum neporovnáváme s cenou jedné objednávky.
					Cenové rozpětí zveřejníme až z nejméně pěti různých zakázek; jinak ho vynecháme, aby
					nevznikalo zavádějící srovnání.
				</p>
			</div>
		</section>
	);
}
